import { Add, Edit } from "@mui/icons-material";
import { Button, IconButton, Switch, Tooltip } from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const GradeMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    // Filter form
    const filterFormik = useFormik({
        initialValues: { search: "", start_date: "", end_date: "" },
        enableReinitialize: true,
    });

    // Get Grade list
    const { data: gradeData, isLoading: loading } = useQuery(
        ["get_grade_master", page, filterFormik.values],
        () =>
            axiosInstance.get(API_URLS.get_grade_master, {
                params: {
                    search: filterFormik.values.search,
                    start_date: filterFormik.values.start_date,
                    end_date: filterFormik.values.end_date,
                    page,
                    count: 10,
                },
            }),
        { refetchOnWindowFocus: false }
    );

    const gradeList = gradeData?.data?.response || [];

    // Formik for Add/Edit Grade
    const fk = useFormik({
        initialValues: {
            name: "",
            description: "N/A",
            status: "1",
        },
        onSubmit: async (values) => {
            if (editData) return updateGrade(values);
            return createGrade(values);
        },
    });

    // CREATE
    const createGrade = async (values) => {
        try {
            const reqBody = {
                m08_gd_name: values.name,
                m08_gd_des: values.description,
                m08_gd_status: "1",
            };

            const res = await axiosInstance.post(API_URLS.create_grade, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_grade_master");
                handleClose();
            }
        } catch {
            toast.error("Failed to create grade");
        }
    };

    // UPDATE
    const updateGrade = async (values) => {
        try {
            const reqBody = {
                m08_gd_id: editData.m08_gd_id,
                m08_gd_name: values.name,
                m08_gd_des: values.description,
                m08_gd_status: values.status,
            };

            const res = await axiosInstance.post(API_URLS.update_grade, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_grade_master");
                handleClose();
            }
        } catch {
            toast.error("Failed to update grade");
        }
    };

    // STATUS TOGGLE
    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = { m08_gd_id: row.m08_gd_id, m08_gd_status: checked ? 1 : 0 };
            const res = await axiosInstance.post(API_URLS.update_grade_status, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) queryClient.invalidateQueries("get_grade_master");
        } catch {
            toast.error("Failed to update status");
        }
    };


    const handleExport = async () => {
    try {
      const res = await axiosInstance.get(API_URLS.grade_status_excel, {
        search: filterFormik.values.search || "",
        start_date: filterFormik.values.start_date || "",
        end_date: filterFormik.values.end_date || "",
      })
      const fileUrl = res?.data?.response?.url;
      window.open(fileUrl, "_blank")
    }
    catch (e) {
      console.log("Something went wrong")
    }
  }

    // Dialog control
    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            name: row.m08_gd_name,
            description: row.m08_gd_des,
            status: String(row.m08_gd_status),
        });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    // TABLE HEADERS
    const tableHead = ["S.No", "Grade Name", "Description", "Status", "Actions", "Created Date"];

    const tableRows = gradeList?.data?.map((item, index) => [
        index + 1,
        item.m08_gd_name || "--",
        item.m08_gd_des || "--",
        <Switch
            checked={item.m08_gd_status === 1}
            onChange={(e) => toggleStatus(item, e.target.checked)}
            color="success"
        />,
        <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(item)}>
                <Edit className="text-blue-600" />
            </IconButton>
        </Tooltip>,
        moment(item.m08_gd_created_at).format("DD-MM-YYYY"),
    ]);

    return (
        <div className="mx-5">
            <div className="flex justify-between items-center p-1 mb-5 ">
                <p className="font-bold text-xl">Grade Master</p>
                <Button variant="contained" className="!bg-[#00008b]"  startIcon={<Add />} onClick={handleOpen}>
                    Add Grade
                </Button>
            </div>

            <CustomFilter
                formik={filterFormik}
                onFilter={() => {
                    setPage(1);
                    queryClient.invalidateQueries("get_grade_master");
                }}
                onClear={() => {
                    filterFormik.resetForm();
                    setPage(1);
                    queryClient.invalidateQueries("get_grade_master");
                }}
                onExport={handleExport}
            />

            <CustomTable tablehead={tableHead} tablerow={tableRows} isLoading={loading} />

            <CustomToPagination setPage={setPage} page={page} data={gradeList} />

            <CustomDialog
                open={open}
                onClose={handleClose}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Grade" : "Add Grade"}
                formik={fk}
                fields={[
                    { name: "name", label: "Grade Name" },
                    { name: "description", label: "Description" },
                ]}
            />
        </div>
    );
};

export default GradeMaster;
